import React, { memo } from "react";
import BaseDropdown, { BaseDropdownProps } from "~/components/dropdowns/BaseDropdown";
import { BaseOption } from "~/hooks/useDropdown";
import {
  OPT_CLOTHING_TYPES,
  OPT_ETHNICITY,
  OPT_EYE_COLOR,
  OPT_HAIR_COLOR,
  OPT_HAIR_LENGTH,
  OPT_JOB_STATUS,
} from "~/components/dropdowns/options";
import { theme } from "@chakra-ui/react";
import { useBreakpointValue } from "~/components/elements";
import { useSelector } from "react-redux";
import { RootState } from "~/redux/root-reducer";

interface Dropdown
  extends Omit<BaseDropdownProps<any>, "name" | "getDataSelector" | "onSearchAction" | "isFetchOneTimeOnly"> {
  name?: string;
}

export const BooleanDropdown = memo(
  ({
    name = "bool",
    defaultValue,
    trueLabel = "Yes",
    falseLabel = "No",
    ...rest
  }: Dropdown & { trueLabel?: string; falseLabel?: string }) => {
    const options: BaseOption[] = [
      { label: trueLabel, value: true },
      { label: falseLabel, value: false },
    ];

    const defaultOption = typeof defaultValue !== undefined ? options.find(o => o.value === defaultValue) : undefined;

    return <BaseDropdown name={name} options={options} defaultOption={defaultOption} {...rest} />;
  }
);

export const JobStatusDropdown = memo(({ name = "jobStatus", ...rest }: Dropdown) => (
  <BaseDropdown name={name} options={OPT_JOB_STATUS} {...rest} />
));

export const ClothingTypeDropdown = memo(
  ({ name = "clothingType", except = [], ...rest }: { except: string[] } & Dropdown) => {
    let options = [...OPT_CLOTHING_TYPES];
    if (except.length > 0) {
      options = OPT_CLOTHING_TYPES.filter(o => except.indexOf(o.value) === -1);
    }
    return (
      <BaseDropdown
        placeholder=""
        // Avoid being clipped by dashboard layout
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: theme.zIndices.dropdown }) }}
        isClearable={false}
        variant="outline"
        name={name}
        options={options}
        {...rest}
      />
    );
  }
);

export const OutfitDropdown = memo(({ name = "outfit", except = [], ...rest }: { except?: string[] } & Dropdown) => {
  const height = useBreakpointValue<any>({ base: "2.5rem", md: "3rem" });
  const outfitData = useSelector((state: RootState) => state.dashboard.outfitData);
  let options = [...outfitData];
  if (except.length > 0) {
    options = outfitData.filter(o => except.indexOf(o.name) === -1);
  }

  return (
    <BaseDropdown
      placeholder="Please choose"
      // Avoid being clipped by dashboard layout
      menuPortalTarget={document.body}
      styles={{
        menuPortal: base => ({ ...base, zIndex: theme.zIndices.dropdown }),
      }}
      chakraStyles={{ control: () => ({ h: height }), option: () => ({ h: height }) }}
      isClearable
      isSearchable={false}
      variant="outline"
      name={name}
      options={options}
      {...rest}
    />
  );
});

export const EyeColorDropdown = memo(({ name = "eyeColor", ...rest }: Dropdown) => {
  const height = useBreakpointValue<any>({ base: "2.5rem", md: "3rem" });

  return (
    <BaseDropdown
      name={name}
      options={OPT_EYE_COLOR}
      chakraStyles={{ control: () => ({ h: height }), option: () => ({ h: height }) }}
      isClearable={false}
      isSearchable={false}
      {...rest}
    />
  );
});

export const EthnicityDropdown = memo(({ name = "ethnicity", ...rest }: Dropdown) => {
  const height = useBreakpointValue<any>({ base: "2.5rem", md: "3rem" });

  return (
    <BaseDropdown
      name={name}
      options={OPT_ETHNICITY}
      chakraStyles={{ control: () => ({ h: height }), option: () => ({ h: height }) }}
      isClearable={false}
      isSearchable={false}
      {...rest}
    />
  );
});

export const HairColorDropdown = memo(({ name = "hairColor", ...rest }: Dropdown) => {
  const height = useBreakpointValue<any>({ base: "2.5rem", md: "3rem" });

  return (
    <BaseDropdown
      name={name}
      options={OPT_HAIR_COLOR}
      chakraStyles={{ control: () => ({ h: height }), option: () => ({ h: height }) }}
      isClearable={false}
      isSearchable={false}
      {...rest}
    />
  );
});

export const HairLengthDropdown = memo(({ name = "hairLength", ...rest }: Dropdown) => {
  const height = useBreakpointValue<any>({ base: "2.5rem", md: "3rem" });

  return (
    <BaseDropdown
      name={name}
      options={OPT_HAIR_LENGTH}
      chakraStyles={{ control: () => ({ h: height }), option: () => ({ h: height }) }}
      isClearable={false}
      isSearchable={false}
      {...rest}
    />
  );
});
